import React, { useEffect, useRef, useState } from "react";
import { useCallback } from "react";
import { useMemo } from "react";
import styles from "../styles/search-bar.module.css";
import { debounce, isEmpty as _isEmpty } from "lodash-es";
import { AppSettings } from "../lib/get-app-settings";
import { searchHotkey } from "./layout-sidebar.light";
import Search from "./search";

type TSearchBarProps = {
  baseUrl: string;
  className?: string;
  title?: string;
  defaultValue?: string; // TODO: Make use of this
  appSettings: AppSettings;
};

export default function SearchBar({
  baseUrl,
  className,
  title,
  defaultValue,
  appSettings,
}: TSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [focused, setFocused] = useState(false);

  const changeFocus = useMemo(() => debounce(setFocused, 10), [setFocused]);

  const handleFocus = useMemo(
    () => debounce(() => changeFocus(true), 5),
    [changeFocus]
  );

  const handleBlur = useCallback(() => changeFocus(false), [changeFocus]);

  const handleClose = useCallback(() => changeFocus(false), [changeFocus]);

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLElement>>(
    (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        (document.activeElement as HTMLElement)?.blur();
      }
    },
    []
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!searchHotkey.matches(event)) return;
      inputRef.current?.focus();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      className={`${className || ""} ${styles.searchBar} ${
        focused ? styles.focusedSearchBar : ""
      }`}
    >
      <label>
        {title && (
          <span className={styles.searchBarTitle} lang={appSettings.language}>
            {title}
          </span>
        )}

        <Search
          className={styles.searchBarInputContainer}
          appSettings={appSettings}
          baseUrl={baseUrl}
          defaultValue={defaultValue}
          inputRef={inputRef}
          collapsed={!focused}
          onClose={handleClose}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </label>
    </div>
  );
}
